import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const TICKETS_API = '/api/tickets';
const OPERATORS_API = '/api/operators';
const MASTER_DATA_API = '/api/master-data';
const DEPARTMENTS_API = '/api/departments';
const SERVICES_API = '/api/services';
const PREDEFINED_REPLIES_API = '/api/predefined-replies';

/* ── tiny SVG icon helper ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    x: ['M18 6 6 18', 'M6 6l12 12'],
    plus: ['M12 5v14', 'M5 12h14'],
    chevronDown: ['M6 9l6 6 6-6'],
    tag: ['M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z', 'M7 7h.01'],
    bold: ['M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zm0 8h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z'],
    italic: ['M19 4h-9', 'M14 20H5', 'M15 4L9 20'],
    link: ['M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'],
    image: ['M3 3h18v18H3z', 'M3 15l5.12-5.12a3 3 0 0 1 4.24 0L15 12', 'M11 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0'],
    list: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
    olist: ['M9 6h11', 'M9 12h11', 'M9 18h11', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
    history: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5', 'M12 7v5l4 2'],
    alignLeft: ['M21 6H3', 'M15 12H3', 'M17 18H3'],
    alignCenter: ['M21 6H3', 'M19 12H5', 'M17 18H7'],
    alignRight: ['M21 6H3', 'M21 12H9', 'M21 18H11'],
    alignJustify: ['M21 6H3', 'M21 12H3', 'M21 18H3'],
    font: ['M4 20h16', 'M7 16l4.4-12h1.2l4.4 12', 'M8 12h8'],
    pencil: ['M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'],
    paperclip: ['M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48'],
};

/* ── Rich text toolbar ── */
function RichToolbar({ editorRef }) {
    const exec = (cmd, val = null) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, val);
    };
    const btn = (label, action, title) => (
        <button type="button" title={title} onClick={action}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', color: '#374151' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >{label}</button>
    );
    const sep = () => <div style={{ width: '1px', height: '18px', background: '#e2e8f0', margin: '0 2px' }} />;

    return (
        <div style={{ borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
            {/* Menu row */}
            <div style={{ display: 'flex', gap: '2px', padding: '4px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '13.36px', color: '#374151' }}>
                {['File', 'Edit', 'View', 'Insert', 'Format', 'Tools', 'Table'].map(m => (
                    <button key={m} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', borderRadius: '4px', fontSize: '13.36px', color: '#374151' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >{m}</button>
                ))}
                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px', borderRadius: '4px', fontSize: '13.36px', color: '#3b82f6', marginLeft: '8px' }}>
                    Save Message as Predefined Reply
                </button>
            </div>
            {/* Toolbar row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 12px', flexWrap: 'wrap' }}>
                <select style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 4px', fontSize: '13.36px', height: '26px', background: '#fff', color: '#374151' }} onChange={e => exec('fontName', e.target.value)}>
                    <option value="Inter">System Font</option>
                    <option value="Arial">Arial</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Georgia">Georgia</option>
                </select>
                <select style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 4px', fontSize: '13.36px', height: '26px', background: '#fff', color: '#374151', width: '56px' }} onChange={e => exec('fontSize', e.target.value)}>
                    <option value="2">10pt</option>
                    <option value="3">12pt</option>
                    <option value="4">14pt</option>
                    <option value="5">18pt</option>
                    <option value="6">24pt</option>
                </select>
                {sep()}
                {btn(<span style={{ fontSize: '14.49px', fontWeight: 700, color: '#374151' }}>A</span>, () => exec('foreColor', prompt('Color:', '#000000')), 'Font Color')}
                {btn(<span style={{ fontSize: '14.49px', color: '#374151', textDecoration: 'underline', textDecorationColor: '#fbbf24' }}>A</span>, () => exec('hiliteColor', prompt('Highlight:', '#ffff00')), 'Highlight')}
                {sep()}
                {btn(<b style={{ fontSize: '14.49px' }}>B</b>, () => exec('bold'), 'Bold')}
                {btn(<i style={{ fontSize: '14.49px' }}>I</i>, () => exec('italic'), 'Italic')}
                {sep()}
                {btn(<Icon d={icons.alignLeft} size={13} />, () => exec('justifyLeft'), 'Align Left')}
                {btn(<Icon d={icons.alignCenter} size={13} />, () => exec('justifyCenter'), 'Center')}
                {btn(<Icon d={icons.alignRight} size={13} />, () => exec('justifyRight'), 'Align Right')}
                {btn(<Icon d={icons.alignJustify} size={13} />, () => exec('justifyFull'), 'Justify')}
                {sep()}
                {btn(<Icon d={icons.image} size={13} />, () => exec('insertImage', prompt('Image URL:')), 'Image')}
                {btn(<Icon d={icons.link} size={13} />, () => exec('createLink', prompt('URL:')), 'Link')}
                {sep()}
                {btn(<Icon d={icons.list} size={13} />, () => exec('insertUnorderedList'), 'Bullet List')}
                {btn(<Icon d={icons.olist} size={13} />, () => exec('insertOrderedList'), 'Numbered List')}
                {sep()}
                {btn(<Icon d={icons.history} size={13} />, () => exec('undo'), 'Undo')}
            </div>
        </div>
    );
}

/* ── Main Component ── */
export default function CreateTicket({ editTicket, onSaved, onCancel, user }) {
    const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Current User';

    const [form, setForm] = useState({
        subject: editTicket?.subject || '',
        description: editTicket?.description || '',
        contact: editTicket?.contact?._id || editTicket?.contact || '',
        contactName: editTicket?.contactName || '',
        contactEmail: editTicket?.contactEmail || '',
        department: editTicket?.department?._id || editTicket?.department || '',
        service: editTicket?.service?._id || editTicket?.service || '',
        priority: editTicket?.priority?._id || editTicket?.priority || '',
        status: editTicket?.status?._id || editTicket?.status || '',
        tags: editTicket?.tags ? (Array.isArray(editTicket.tags) ? editTicket.tags.join(', ') : editTicket.tags) : '',
        assignee: userName
    });

    const [ticketWithoutContact, setTicketWithoutContact] = useState(false);
    const [operators, setOperators] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [services, setServices] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [predefinedReplies, setPredefinedReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contactSearch, setContactSearch] = useState(editTicket?.contactName || '');
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const [ops, depts, servs, md, replies] = await Promise.all([
                    axios.get(OPERATORS_API),
                    axios.get(DEPARTMENTS_API),
                    axios.get(SERVICES_API),
                    axios.get(MASTER_DATA_API),
                    axios.get(PREDEFINED_REPLIES_API).catch(() => ({ data: [] }))
                ]);
                setOperators(ops.data);
                setDepartments(depts.data);
                setServices(servs.data);
                setPriorities(md.data.filter(i => i.category === 'ticket-priority' && i.active !== false));
                setStatuses(md.data.filter(i => i.category === 'ticket-status' && i.active !== false));
                setPredefinedReplies(Array.isArray(replies.data) ? replies.data.filter(r => r.active !== false) : []);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchMasterData();
    }, []);

    useEffect(() => {
        // Set editor content when loading is finished and editorRef is attached
        if (!loading && editTicket?.description && editorRef.current) {
            editorRef.current.innerHTML = editTicket.description;
        }

        // If editing and we have a contact ID but no name strings (legacy tickets)
        // fetch the operator data to populate these fields
        if (!loading && editTicket?.contact && (!editTicket.contactName || !editTicket.contactEmail)) {
            const api = `/api/operators/${editTicket.contact._id || editTicket.contact}`;
            axios.get(api).then(res => {
                const op = res.data;
                const name = op.companyName || op.contactPersonName || '';
                const email = op.primaryEmail || '';
                setForm(prev => ({
                    ...prev,
                    contactName: name,
                    contactEmail: email
                }));
                if (!contactSearch) setContactSearch(name);
            }).catch(e => console.error("Error fetching legacy contact:", e));
        }
    }, [editTicket, loading]);

    const handleContactSelect = (op) => {
        const name = op.companyName || op.contactPersonName || '';
        const email = op.primaryEmail || '';
        setForm({
            ...form,
            contact: op._id,
            contactName: name,
            contactEmail: email,
            department: op.department?._id || op.department || form.department,
        });
        setContactSearch(name);
        setShowContactDropdown(false);
    };

    const clearContact = () => {
        setForm({ ...form, contact: '', contactName: '', contactEmail: '' });
        setContactSearch('');
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...files]);
    };

    const handlePredefinedReply = (replyId) => {
        if (!replyId) return;
        const reply = predefinedReplies.find(r => r._id === replyId);
        if (reply && editorRef.current) {
            editorRef.current.innerHTML = reply.content;
        }
    };

    const handleTagClick = (statusName) => {
        const currentTags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
        if (currentTags.includes(statusName)) {
            // Remove the tag if already present
            setForm({ ...form, tags: currentTags.filter(t => t !== statusName).join(', ') });
        } else {
            // Add the tag
            setForm({ ...form, tags: [...currentTags, statusName].join(', ') });
        }
    };

    const handleSave = async () => {
        if (!form.subject.trim()) { alert('Subject is required'); return; }
        const description = editorRef.current?.innerHTML || '';
        const tags = typeof form.tags === 'string'
            ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
            : form.tags;

        // Only send fields that exist in the Ticket schema
        const payload = {
            subject: form.subject.trim(),
            description: description,
            tags: tags,
            contact: form.contact || '',
            contactName: form.contactName || '',
            contactEmail: form.contactEmail || '',
            department: form.department || undefined,
            service: form.service || undefined,
            priority: form.priority || undefined,
            status: form.status || undefined
        };

        try {
            if (editTicket) {
                await axios.put(`${TICKETS_API}/${editTicket._id}`, payload);
            } else {
                await axios.post(TICKETS_API, payload);
            }
            onSaved();
        } catch (err) {
            console.error('Save error:', err);
            alert('Error saving ticket');
        }
    };

    if (loading) return <div style={{ padding: 40, color: '#64748b' }}>Loading...</div>;

    const filteredOperators = operators.filter(op =>
        (op.contactPersonName || '').toLowerCase().includes(contactSearch.toLowerCase()) ||
        (op.companyName || '').toLowerCase().includes(contactSearch.toLowerCase())
    );

    const currentTags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

    /* ── shared input style ── */
    const inputStyle = {
        width: '100%', boxSizing: 'border-box', height: '42px',
        border: '1px solid #e2e8f0', borderRadius: '10px',
        padding: '0 14px', fontSize: '14px', color: '#1e293b', background: '#fff',
        outline: 'none', transition: 'all 0.2s ease'
    };
    const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'auto' };
    const labelStyle = { fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

    return (
        <div style={{
            background: '#f8fafc',
            height: 'calc(100vh - 65px)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            color: '#1e293b',
            fontFamily: "'Inter', sans-serif",
            scrollBehavior: 'smooth',
            willChange: 'transform'
        }}>
            {/* Structured Header */}
            <div style={{
                background: '#fff',
                borderBottom: '1px solid #e2e8f0',
                padding: '16px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onCancel} style={{
                        background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600'
                    }} onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'} onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                        <Icon d={icons.x} size={16} /> Discard
                    </button>
                    <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
                    <h1 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                        {editTicket ? 'Edit Ticket' : 'Create New Ticket'}
                    </h1>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={handleSave} style={{
                        padding: '10px 28px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: '#fff',
                        fontSize: '14px',
                        cursor: 'pointer',
                        fontWeight: '700',
                        boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.3)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                    }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(79, 70, 229, 0.4)'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(79, 70, 229, 0.3)'; }}>
                        {editTicket ? 'Save Changes' : 'Publish Ticket'}
                    </button>
                </div>
            </div>

            <div style={{ flex: 1, padding: '40px', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '1100px', display: 'flex', gap: '32px' }}>

                    {/* Main Section: Subject & Editor Card */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '32px' }}>

                        {/* Subject Block */}
                        <div style={{ background: '#fff', padding: '32px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <label style={labelStyle}>Ticket Subject</label>
                            <input
                                style={{
                                    width: '100%',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '16px 20px',
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: '#0f172a',
                                    outline: 'none',
                                    background: '#fcfcfd',
                                    transition: 'border-color 0.2s ease'
                                }}
                                onFocus={e => e.currentTarget.style.borderColor = '#4f46e5'}
                                onBlur={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                value={form.subject}
                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                placeholder="What is the issue about?..."
                            />
                        </div>

                        {/* Editor Block */}
                        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                            <div style={{ padding: '20px 32px', borderBottom: '1px solid #f1f5f9', background: '#fcfcfd', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon d={icons.pencil} size={16} />
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Issue Description</span>
                            </div>

                            <RichToolbar editorRef={editorRef} />

                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                style={{
                                    minHeight: '450px',
                                    padding: '32px',
                                    outline: 'none',
                                    color: '#334155',
                                    fontSize: '16px',
                                    lineHeight: '1.8',
                                    background: '#fff'
                                }}
                            />

                            <div style={{ padding: '16px 32px', borderTop: '1px solid #f1f5f9', background: '#fcfcfd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <select
                                    style={{
                                        ...selectStyle,
                                        maxWidth: '240px',
                                        height: '32px',
                                        fontSize: '12px',
                                        borderRadius: '8px',
                                        borderColor: '#e2e8f0',
                                        padding: '0 10px'
                                    }}
                                    onChange={e => handlePredefinedReply(e.target.value)}
                                    defaultValue=""
                                >
                                    <option value="">+ Add Predefined Template</option>
                                    {predefinedReplies.map(r => (
                                        <option key={r._id} value={r._id}>{r.title}</option>
                                    ))}
                                </select>
                                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
                                    Autosaved just now
                                </div>
                            </div>
                        </div>

                        {/* Attachments Block */}
                        <div style={{ background: '#fff', padding: '32px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <label style={labelStyle}>Attachments</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                                {attachments.map((f, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: '10px', background: '#f1f5f9',
                                        padding: '8px 14px', borderRadius: '12px', fontSize: '13px', color: '#475569',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Icon d={icons.paperclip} size={14} />
                                        {f.name}
                                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8' }} onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}>
                                            <Icon d={icons.x} size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={() => fileInputRef.current?.click()}
                                style={{
                                    padding: '24px',
                                    border: '2px dashed #e2e8f0',
                                    borderRadius: '16px',
                                    background: '#fcfcfd',
                                    fontSize: '14px',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    width: '100%',
                                    textAlign: 'center',
                                    transition: 'all 0.2s'
                                }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#f5f3ff'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#fcfcfd'; }}>
                                <Icon d={icons.plus} size={20} style={{ marginBottom: '8px', display: 'block', margin: '0 auto' }} />
                                Click or drag files to upload
                            </button>
                            <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileChange} />
                        </div>
                    </div>

                    {/* Sidebar Sections */}
                    <div style={{ width: '340px', display: 'flex', flexDirection: 'column', gap: '32px' }}>

                        {/* Requester Block */}
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <label style={{ ...labelStyle, marginBottom: 0 }}>Contact / Owner</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <input type="checkbox" checked={ticketWithoutContact} onChange={e => setTicketWithoutContact(e.target.checked)} />
                                    No Link
                                </label>
                            </div>

                            {!ticketWithoutContact ? (
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            style={{ ...inputStyle, background: '#f8fafc', paddingLeft: '36px' }}
                                            value={contactSearch}
                                            onChange={e => { setContactSearch(e.target.value); setShowContactDropdown(true); }}
                                            onFocus={() => setShowContactDropdown(true)}
                                            placeholder="Search customer..."
                                        />
                                        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                            <Icon d={icons.chevronDown} size={14} />
                                        </div>
                                    </div>
                                    {showContactDropdown && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
                                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', marginTop: '8px', maxHeight: '240px', overflowY: 'auto'
                                        }}>
                                            {filteredOperators.length === 0
                                                ? <div style={{ padding: '16px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>No results</div>
                                                : filteredOperators.map(op => (
                                                    <div key={op._id}
                                                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                                                        onClick={() => handleContactSelect(op)}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                                    >
                                                        <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{op.companyName || op.contactPersonName}</div>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>{op.primaryEmail}</div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ padding: '12px', borderRadius: '10px', background: '#f8fafc', color: '#94a3b8', fontSize: '13px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>Anonymous / Unlinked</div>
                            )}
                        </div>

                        {/* Classification Block */}
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <label style={{ ...labelStyle, marginBottom: '20px' }}>Classification</label>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div>
                                    <label style={labelStyle}>Priority</label>
                                    <select style={selectStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                        <option value="">Unassigned</option>
                                        {priorities.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Department</label>
                                    <select style={selectStyle} value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                                        <option value="">Global</option>
                                        {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Service Category</label>
                                    <select style={selectStyle} value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}>
                                        <option value="">Core System</option>
                                        {services.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Assignee & Labels Block */}
                        <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                            <div style={{ marginBottom: '24px' }}>
                                <label style={labelStyle}>Owner</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800' }}>
                                        {userName.charAt(0)}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '700' }}>{userName}</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>Manager</div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon d={icons.tag} size={14} /> Tags
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                    {statuses.map(s => {
                                        const isActive = currentTags.includes(s.name);
                                        return (
                                            <button
                                                key={s._id}
                                                type="button"
                                                onClick={() => handleTagClick(s.name)}
                                                style={{
                                                    padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
                                                    cursor: 'pointer', border: '1px solid',
                                                    borderColor: isActive ? (s.color || '#4f46e5') : '#e2e8f0',
                                                    background: isActive ? (s.color || '#4f46e5') : '#fcfcfd',
                                                    color: isActive ? '#fff' : '#64748b',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {s.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Backdrop for dropdown */}
            {showContactDropdown && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 190 }} onClick={() => setShowContactDropdown(false)} />
            )}
        </div>
    );
}
